import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import AttachmentPreview from '../AttachmentPreview';
import GeckoRequest from 'services/gecko-request';
import { renderError } from 'services/gecko-error';
import { GeckoToastService, GeckoBtn, GeckoModalService } from 'ui';
import { EditorState, convertToRaw, convertFromRaw } from 'draft-js';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import GeckoEditor from './Components/GeckoEditor';
import ChannelSelector from './Components/ChannelSelector';
import { selectNoteChannel } from 'reducers/channels_selector';
import DragUpload from 'components/DragUpload';
import SavedReplySelector from 'components/CreateSelector/SavedReplySelector.jsx';
import EmojiPicker from 'components/CreateSelector/EmojiPicker';
import TagBuilder from '../TagBuilder';
import { Collapse } from 'ui';
import Meta from './Components/Meta';
import { RBfuncs } from './ReplyBoxFuncs';
import ReactDOM from 'react-dom';
import GeckoSocket from 'services/gecko-socket';
import CCForm from '../CCForm';
import ComposeModal from 'routes/conversations/components/ComposeModal';
import { replaceTemplateTags, insertImageLink } from './logic';
import {
  getShouldStartNewConversation,
  getConversationRichTextTranscript,
  getMessageText,
  openStaticModalWith,
  sendMessage,
  forwardMessage,
} from 'routes/conversations/utils';
import useProgressFileUpload from 'components/useProgressFileUpload';
import { useRedux } from 'services/gecko-store/redux';
import { CONSTS } from 'constants/index.js';
import CONSTANTS from 'chat-constants';
import { GECKO_CONSTS } from 'ui';

import './index.scss';
import Loader from 'components/Loader';
import AiSuggestionModal from 'routes/conversations/components/AISuggestionModal';
import { useDispatch, useSelector } from 'react-redux';

const ReplyBox = (props) => {
  const {
    autoFocus,
    availableChannels,
    bcc = [],
    cc = [],
    compileTemplateTags,
    contact,
    conversationId,
    editorMode,
    editorState,
    handleSubmission,
    meta,
    onRecipientChannelIdSelected,
    placeholder,
    recipientChannel,
    templateContext,
    toolbarClassName,
    updateBCC,
    updateCallbacks,
    updateCC,
    agent,
    botSuggestions = [],
    handleAiSuggestion,
  } = props;

  const [attachments, setAttachmentsState] = useState([]);
  const [progressState, setProgressState] = useState(null);
  const [highlightedText, setHighlightedText] = useState(false);
  const [typing, setTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [excludeSignature, setExcludeSignature] = useState(false);
  const [tagBuilderShowing, setTagBuilderShowing] = useState(false);
  const [CCShowing, setCCShowing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditorMaximised, setIsEditorMaximised] = useState(false);

  const [savedReplies, setSavedReplies] = useState([]);
  const [textMode, setTextMode] = useState(CONSTS.EDITOR_MODE_TYPE.RICH);
  const setAttachments = (attachments) => {
    setAttachmentsState(attachments);
    if (updateCallbacks.attachments) {
      updateCallbacks.attachments(attachments);
    }
  };
  const rsvpLink = useSelector((state) => state.conversationsRoute.rsvpLink);
  const apiUrl = useRedux(
    (state) => state.appState.apiUrl || process.env.REACT_APP_API_URL
  );
  const defaultHeaders = useRedux((state) => state.appState.defaultHeaders);
  const noteChannel = useRedux((state) => selectNoteChannel(state, props));
  const dispatch = useDispatch();

  const insertLink = async () => {
    const newEditorState = await RBfuncs.insertLink(
      'Your RSVP page',
      rsvpLink,
      { editorState, editorMode }
    );
    onEditorStateChange(newEditorState);
    dispatch({ type: 'SET_RSVP_LINK', payload: '' });
  };

  useEffect(() => {
    if (rsvpLink) {
      insertLink();
    }
  }, [rsvpLink]);

  const onIncludeFormResponse = async () => {
    try {
      const { blocks, entityMap } = await GeckoRequest.get(
        `/conversation/${conversationId}/form-response-transcript`
      );
      const rawState = convertToRaw(editorState.getCurrentContent());
      const newState = {
        ...rawState,
        blocks: [...rawState.blocks, ...blocks],
        entityMap: { ...rawState.entityMap, ...entityMap },
      };
      onEditorStateChange(
        EditorState.createWithContent(convertFromRaw(newState))
      );
    } catch (error) {
      renderError(error);
    }
  };

  const { isUploading, uploadProgress, startUpload } = useProgressFileUpload({
    uploadURL: `${apiUrl}/message/upload_attachment`,
    requestHeaders: defaultHeaders,
  });

  const editorDiv = useRef(null);
  const draftEditor = useRef(null);

  const setDraftEditorRef = (ref) => (draftEditor.current = ref);
  const setEditorDiv = (ref) => (editorDiv.current = ref);

  const attachmentFileRef = useRef(null);
  useEffect(() => {
    const changeMode = async () => {
      setIsLoading(true);
      const { editorState: newEditorState, attachments } =
        await RBfuncs.stateFromNewMode(textMode, editorMode, editorState);

      if (attachments && attachments.length === 0) {
        setAttachments([]);
      }

      if (newEditorState) {
        onEditorStateChange(newEditorState);
      }

      setTextMode(editorMode);
      setIsLoading(false);
    };
    changeMode();
  }, [editorMode]);

  useEffect(() => {
    if (draftEditor && draftEditor.current && autoFocus === true) {
      draftEditor.current.focusEditor();
    }
  }, [draftEditor, conversationId]);

  const sendingIsDisabled =
    (recipientChannel === null && !props.noteMode.on) || isSending || isLoading;

  const currentChannelIsFormResponse =
    props.lastMessageChannel?.type === CONSTANTS.CHANNEL.TYPE_FORM_RESPONSE;

  const onEditorStateChange = (editorState, callback = () => {}) => {
    const _newEditorState = compileTemplateTags
      ? replaceTemplateTags(editorState, templateContext)
      : editorState;
    const newEditorState = EditorState.push(
      _newEditorState,
      _newEditorState.getCurrentContent(),
      _newEditorState.getLastChangeType()
    );
    const selection = newEditorState.getSelection();
    const highlightedTextLength =
      selection.getStartOffset() - selection.getEndOffset();
    const hasHighlightedText = highlightedTextLength !== 0;
    setHighlightedText(hasHighlightedText);
    updateCallbacks.editorState(newEditorState);
    callback();
  };

  const clearBlockAndSetType = (type) => {
    onEditorStateChange(
      RBfuncs.clearBlockContent(
        type,
        RBfuncs.toggleBlockType(type, editorState)
      )
    );
  };

  const clearContentStates = () => {
    const clearedEditor = RBfuncs.clearEditor(editorState);

    setAttachments([]);
    setSavedReplies([]);
    onEditorStateChange(clearedEditor);
    setHighlightedText(false);

    draftEditor.current.focusEditor();
  };
  const scrollToEditorBottom = () => {
    const node = ReactDOM.findDOMNode(editorDiv.current);
    if (node) {
      setTimeout(() => {
        let editorNode = node.querySelector('.editor');
        editorNode.scrollTop = editorNode.scrollHeight;
      }, 300);
    }
  };

  const handleDraggedUpload = (links) => {
    if (editorMode === CONSTS.EDITOR_MODE_TYPE.PLAIN) {
      setAttachments([...attachments, ...links.urls]);
    }
    insertImageLink(
      editorState,
      editorMode,
      links,
      onEditorStateChange,
      scrollToEditorBottom
    );
  };

  const removeAttachment = (indexToRemove) => {
    const newAttachments = attachments.filter((_attachment, index) => {
      return index !== indexToRemove;
    });

    setAttachments(newAttachments);
  };

  const handlePastedText = (text) => {
    insertImageLink({ urls: [text] });
  };

  const uploadAttachmentFile = (event, droppedFiles) => {
    const uploadedFiles = droppedFiles || event.target.files;

    if (uploadedFiles.length === 0) {
      console.error('No files selected');
      return;
    }
    const files = [...uploadedFiles];

    startUpload(files)
      .then((responseText) => {
        const fileDetails = JSON.parse(responseText);
        fileDetails.files = files;
        if (editorMode === CONSTS.EDITOR_MODE_TYPE.PLAIN) {
          setAttachments([...attachments, ...fileDetails.urls]);
        }

        insertImageLink(
          editorState,
          editorMode,
          fileDetails,
          onEditorStateChange,
          scrollToEditorBottom
        );
      })
      .catch((error) => {
        try {
          return renderError(JSON.parse(error));
        } catch (_) {
          const toast = new GeckoToastService();
          return toast.error('Failed to upload attachment(s)');
        }
      });
  };

  const handlePastedFiles = (files) => {
    uploadAttachmentFile(null, files);
  };

  const keyPress = () => {
    if (typing) {
      return;
    }

    if (conversationId) {
      GeckoSocket.emit('user_typing', Number(conversationId));
    }
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
    }, 1000);
  };

  const submitContent = (event) => {
    if (sendingIsDisabled) {
      return;
    }
    setIsSending(true);
    if (event) {
      event.preventDefault();
    }
    if (RBfuncs.isInvalidMessage(editorState, attachments)) {
      new GeckoToastService().error('Message has no content');
      setIsSending(false);
      return;
    }
    //recipientChannel is not supplied for compose modal so we need to fallback to the avaialable channels as they are of a single type
    const channelForLengthCheck = recipientChannel || availableChannels[0];
    if (RBfuncs.isTooLong(editorState, channelForLengthCheck)) {
      new GeckoToastService().error(
        'Messages on this channel can be no longer than 1600 characters'
      );
      setIsSending(false);
      return;
    }
    if (handleSubmission) {
      handleSubmission(
        RBfuncs.composeSubmissionObject(
          editorState,
          editorMode,
          attachments,
          props,
          noteChannel,
          props.noteMode,
          { excludeSignature, savedReplies }
        )
      ).then(() => {
        setIsSending(false);
        clearContentStates();
      });
    } else {
      setIsSending(false);
    }
  };

  const toggleList = (event) => {
    onEditorStateChange(RBfuncs.toggleList(event, editorState));
  };

  const getEditor = () => {
    return (
      <GeckoEditor
        attachments={attachments}
        clearBlockAndSetType={clearBlockAndSetType}
        editorMode={editorMode}
        editorState={editorState}
        handlePastedFiles={handlePastedFiles}
        highlightedText={highlightedText}
        handlePastedText={handlePastedText}
        noteMode={props.noteMode}
        onEditorStateChange={onEditorStateChange}
        onKeyPress={keyPress}
        setEditorRef={setDraftEditorRef}
        setEditorDiv={setEditorDiv}
        submitEditorContent={submitContent}
        toggleList={toggleList}
        toolbarClassName={toolbarClassName}
        placeholder={placeholder}
        isEditorMaximised={isEditorMaximised}
      />
    );
  };

  const getTagBuilder = () => {
    if (tagBuilderShowing && templateContext) {
      return <TagBuilder context={templateContext} onInsert={insertTag} />;
    }
  };

  const insertTag = (tag) => {
    const { templateContext, compileTemplateTags } = props;
    onEditorStateChange(
      RBfuncs.insertTag(tag, editorState, {
        templateContext,
        compileTemplateTags,
      })
    );
  };

  const getCCSettings = () => {
    return (
      <CCForm cc={cc} bcc={bcc} onCCChange={updateCC} onBCCChange={updateBCC} />
    );
  };

  const getMaximiseButton = () => {
    return (
      <GeckoBtn
        tooltip={{
          name: `${isEditorMaximised ? 'Minimise' : 'Maximise'} Editor`,
          placement: 'top',
        }}
        handler={() => setIsEditorMaximised(!isEditorMaximised)}
        icon={`fas ${isEditorMaximised ? 'fa-compress-alt' : 'fa-expand-alt'}`}
        className="maximise-btn"
        type={GECKO_CONSTS.BTN_TYPE_NAKED}
      />
    );
  };

  const setAttachmentFileRef = (ref) => {
    attachmentFileRef.current = ref;
  };
  const renderSavedReplies = () => {
    const stageReply = async (reply) => {
      setIsLoading(true);
      const newEditorState = await RBfuncs.insertSavedReply(reply, {
        editorState,
        editorMode,
      });
      onEditorStateChange(newEditorState, () =>
        draftEditor.current.focusEditor()
      );

      if (!savedReplies.includes(reply.id)) {
        setSavedReplies(savedReplies.concat(reply.id));
      }
      setIsLoading(false);
    };
    return <SavedReplySelector onSelect={stageReply} />;
  };

  const renderEmoji = () => {
    const emojiSelected = async (emoji) => {
      setIsLoading(true);
      const newEditorState = await RBfuncs.insertEmoji(emoji, {
        editorState,
      });
      onEditorStateChange(newEditorState, () =>
        draftEditor.current.focusEditor()
      );
      setIsLoading(false);
    };
    return <EmojiPicker onSelect={emojiSelected} />;
  };

  const renderAiSuggestion = () => {
    const insertAiSuggestion = async (aiSuggestion) => {
      setIsLoading(true);
      const newEditorState = await RBfuncs.insertAISuggestion(aiSuggestion, {
        editorState,
        editorMode,
      });
      onEditorStateChange(newEditorState, () =>
        draftEditor.current.focusEditor()
      );
      setIsLoading(false);
    };
    return (
      <AiSuggestionModal
        onInsert={insertAiSuggestion}
        handleAiSuggestion={handleAiSuggestion}
      />
    );
  };

  const toggleTagBuilderShowing = () => {
    setTagBuilderShowing(!tagBuilderShowing);
    setCCShowing(false);
  };

  const openReplyModal = async (channel) => {
    const transcript = await getConversationRichTextTranscript(conversationId);
    const message = convertToRaw(editorState.getCurrentContent());

    const replyWithMessage = sendMessage(
      recipientChannel,
      channel,
      contact.id,
      agent.id,
      conversationId
    );

    const modalProps = {
      message: message,
      transcript: transcript.transcript,
      subject: `RE: ${transcript.subject}`,
      title: 'Reply Via Email',
      sendTo: [contact.email],
      availableChannels: [channel],
      channel: channel.id,
      disableToField: true,
      messageType: 'mailgun',
      sendFunction: replyWithMessage,
    };

    openStaticModalWith(<ComposeModal {...modalProps} />);
  };

  const onChannelSelect = (channel) => {
    const shouldStartNewConversation = getShouldStartNewConversation(
      channel,
      props.lastMessageChannel
    );

    if (shouldStartNewConversation) {
      return openReplyModal(channel);
    }

    return onRecipientChannelIdSelected(channel.id);
  };

  const openForwardingModal = async () => {
    const sendFunction = forwardMessage(conversationId);
    const { transcript, subject } = await getConversationRichTextTranscript(
      conversationId
    );

    const modalProps = {
      message: '',
      transcript: transcript,
      subject: `FW: ${subject}`,
      title: 'Forward Conversation',
      messageType: 'mailgun',
      sendFunction,
    };

    openStaticModalWith(<ComposeModal {...modalProps} />);
  };

  const toggleCCShowing = () => {
    setCCShowing(!CCShowing);
    setTagBuilderShowing(false);
  };

  const toggleNoteMode = () => {
    if (updateCallbacks.noteMode) {
      updateCallbacks.noteMode({ on: !props.noteMode.on });
    }
  };

  const getMetaProps = () => {
    const _metaProps = {
      noteMode: {
        on: props.noteMode.on,
      },
      notes: {
        show: false,
        onClick: toggleNoteMode,
      },
      attachments: {
        show: false,
        onClick: () => attachmentFileRef.current.click(),
        onChange: uploadAttachmentFile,
        setAttachmentFileRef: setAttachmentFileRef,
      },
      downloads: {
        show: false,
        onClick: () => {},
      },
      saved_replies: {
        show: false,
        render: renderSavedReplies,
      },
      emoji: {
        show: false,
        render: renderEmoji,
      },
      mail_merge: {
        show: false,
        onClick: toggleTagBuilderShowing,
      },
      mail_forward: {
        show: false,
        onClick: openForwardingModal,
      },
      mail_cc: {
        show: false,
        onClick: toggleCCShowing,
        hasCCs: Boolean((cc && cc.length !== 0) || (bcc && bcc.length !== 0)),
      },
      exclude_signature: {
        show: false,
        excludeSignature: excludeSignature,
        onClick: () => setExcludeSignature(!excludeSignature),
      },
      progress: {
        value: uploadProgress,
        isUploading: isUploading,
      },
      ai_suggestion: {
        show: false,
        render: renderAiSuggestion,
      },
      send: {
        show: false,
        label: 'Send',
        icon: 'fas fa-paper-plane-top',
        onClick: submitContent,
        disabled: sendingIsDisabled,
      },
    };
    const containerProps = meta;

    const metaProps = Object.entries(_metaProps).reduce(
      (metaProps, [key, value]) => {
        const newMetaValue = { ...value, ...containerProps[key] };
        return { ...metaProps, [key]: newMetaValue };
      },
      _metaProps
    );

    return metaProps;
  };
  const applySuggestion = async (suggestion) => {
    setIsLoading(true);
    const newEditorState = await RBfuncs.insertBotSuggestion(suggestion, {
      editorState,
      editorMode,
    });
    onEditorStateChange(newEditorState, () =>
      draftEditor.current.focusEditor()
    );
    setIsLoading(false);
  };

  const getMeta = () => {
    if (!meta) {
      return null;
    }
    return <Meta {...getMetaProps()} />;
  };
  const responseNameCharLimit = 37;
  const tooltipNameCharLimit = 230;
  const formatName = (name, limit) => {
    return name.length > limit
      ? name.substring(0, limit - 1).concat('...')
      : name;
  };

  const getInner = () => {
    const richTextBarShowing =
      editorMode !== CONSTS.EDITOR_MODE_TYPE.PLAIN && highlightedText;
    const isChannelSelectorHidden =
      props.showChannelSelector === false || richTextBarShowing;
    const showSuggestions = Boolean(props.botSuggestions?.length > 0);

    return (
      <div>
        {showSuggestions ? (
          <div className="BotSuggestions">
            <h2>
              <span>Suggested Bot Answers</span>
            </h2>
            <React.Fragment>
              <div className="row">
                <div className="col">
                  {botSuggestions.map((suggestion, index) => {
                    return (
                      <GeckoBtn
                        key={index}
                        tooltip={{
                          name: `${formatName(
                            suggestion.content,
                            tooltipNameCharLimit
                          )}`,
                          placement: 'top',
                        }}
                        type="light"
                        handler={() => applySuggestion(suggestion)}
                        name={formatName(
                          suggestion.name,
                          responseNameCharLimit
                        )}
                      />
                    );
                  })}
                </div>
              </div>
            </React.Fragment>
          </div>
        ) : null}
        <div
          className={`ReplyBox reply-box z-depth-1 ${
            props.noteMode.on ? 'note-mode' : ''
          } ${richTextBarShowing ? 'with-toolbar' : ''}`}
        >
          <Loader isLoading={isLoading}>
            <div className="row">
              <div className="col">
                <ChannelSelector
                  channels={availableChannels}
                  hidden={isChannelSelectorHidden}
                  noteChannelId={Number(noteChannel.id)}
                  noteMode={props.noteMode}
                  selectedChannel={recipientChannel}
                  onChannelSelected={onChannelSelect}
                  isPickingInitialChannel={currentChannelIsFormResponse}
                  includeFormProp={{
                    currentChannelIsFormResponse,
                    onIncludeFormResponse,
                  }}
                  maximiseButton={getMaximiseButton()}
                />
              </div>
            </div>

            <div className="row no-gutters">
              <div className="col-sm-12">
                {attachments.map((attachment, index) => {
                  return (
                    <span
                      className="ReplyBox__attachment--preview"
                      key={`att_prev_${index}`}
                    >
                      <AttachmentPreview
                        width={100}
                        attachment={attachment}
                        onRemove={() => removeAttachment(index)}
                      />
                    </span>
                  );
                })}
                {getEditor()}
                <Collapse in={tagBuilderShowing}>
                  <div>
                    <hr />
                    {getTagBuilder()}
                  </div>
                </Collapse>
                <Collapse in={CCShowing}>
                  <div>
                    {CCShowing ? (
                      <div>
                        <hr />
                        {getCCSettings()}
                      </div>
                    ) : null}
                  </div>
                </Collapse>
              </div>
            </div>
            {getMeta()}
          </Loader>
        </div>
      </div>
    );
  };

  if (props.noDragUpload) {
    return getInner();
  }

  return (
    <DragUpload
      onUpload={handleDraggedUpload}
      uploadURL={`${apiUrl}/message/upload_attachment`}
      hideProgress={true}
      onProgress={setProgressState}
    >
      {getInner()}
    </DragUpload>
  );
};

ReplyBox.defaultProps = {
  compileTemplateTags: true,
  editorMode: CONSTS.EDITOR_MODE_TYPE.RICH,
  updateCallbacks: {
    editorState: () => {},
    attachments: () => {},
  },
  editorState: EditorState.createEmpty(),
  availableChannels: [],
  noteMode: false,
  showChannelSelector: false,
  autoFocus: false,
  botSuggestions: [],
  handleAiSuggestions: () => {},
};

ReplyBox.propTypes = {
  autoFocus: PropTypes.bool,
  availableChannels: PropTypes.array,
  compileTemplateTags: PropTypes.bool,
  contact: PropTypes.object,
  editorState: PropTypes.object,
  handleSubmission: PropTypes.func,
  meta: PropTypes.object,
  noteMode: PropTypes.object,
  recipientChannel: PropTypes.object,
  onRecipientChannelIdSelected: PropTypes.func,
  templateContext: PropTypes.array,
  toolbarClassName: PropTypes.string,
  updateCallbacks: PropTypes.shape({
    editorState: PropTypes.func,
    attachments: PropTypes.func,
  }),
  conversationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  cc: PropTypes.arrayOf(PropTypes.string),
  bcc: PropTypes.arrayOf(PropTypes.string),
  updateCC: PropTypes.func,
  updateBCC: PropTypes.func,
  editorMode: PropTypes.oneOf(Object.values(CONSTS.EDITOR_MODE_TYPE)),
  showChannelSelector: PropTypes.bool,
  botSuggestions: PropTypes.array,
  handleAiSuggestions: PropTypes.func,
};

export default ReplyBox;
